<template>
    <ResizableContent
        :key="maxGridConversationsWidth + '' + minGridConversationsWidth"
        class="conversations"
        :handles="['mr']"
        :onResize="onResizeConversations"
        :max-width="maxGridConversationsWidth"
        :min-width="minGridConversationsWidth"
        :width="width"
    >
        <div class="head-content">
            <div class="search-area" v-if="searchMode">
                <div class="chat-search-wrapper">
                    <a-input :value="filters.query" @change="querySearch" class="search-input" placeholder="Search">
                        <template #prefix>
                            <div class="material-icons">search</div>
                        </template>
                    </a-input>
                </div>
                <div class="search-close-wrapper">
                    <div class="material-icons" @click="closeSearchMode">close</div>
                </div>
            </div>
            <div class="title-content" v-else>
                <div class="title">
                    <span class="title-text">{{ $t("message.chatsTitle") }}</span>
                    <ContactSorter v-if="!chatListToolbarLocked" />
                </div>
                <div class="actions">
                    <div class="secondary-content">
                        <ContactSwitcherButton />
                        <div class="chat-search-content" v-if="!chatListToolbarLocked">
                            <SupsisButton @click="searchModeOpen" primary class-names="closed-search-button">
                                <div class="material-icons">search</div>
                            </SupsisButton>
                        </div>
                        <div class="material-icons btnLock" @click="toggleLock" v-if="chatListToolbarLocked">lock</div>
                        <div class="material-icons btnLock" @click="toggleLock" v-else>lock_open</div>
                    </div>
                </div>
            </div>
        </div>
        <ContactListFilterToolBar v-if="contactListFilterMode === 'custom'" v-show="!chatListToolbarLocked">
            <template slot="extra">
                <ContactGroupButton v-if="contactListFilterMode === 'custom'" />
            </template>
        </ContactListFilterToolBar>

        <div class="filters-tabs">
            <div
                class="filter-tab"
                :class="{ active: !filters.showUnread && contactListFilterMode === 'custom' }"
                @click="showAll"
            >
                {{ $t("chatbotv2.all") }}
                <span v-if="contactListFilterMode === 'custom'">{{ getStatsNormalized().all }}</span>
            </div>
            <div
                class="filter-tab"
                :class="{ active: filters.showUnread && contactListFilterMode === 'custom' }"
                @click="showUnread"
            >
                {{ $t("mail.unread") }}
                <span v-if="contactListFilterMode === 'custom'">{{ getStatsNormalized().unread }}</span>
            </div>
            <div class="filter-tab" :class="{ active: contactListFilterMode === 'queue' }" @click="showQueue">
                {{ $t("chatWindow.waiting") }}
                <span v-if="contactListFilterMode === 'queue'">{{ getStatsNormalized().all }}</span>
                <span v-else>{{ systemStats.waiting + systemStats.free + systemStats.late }}</span>
            </div>
            <div class="filter-tab" :class="{ active: contactListFilterMode === 'chatbot' }" @click="showChatbot">
                Chatbot
                <span v-if="contactListFilterMode === 'chatbot'">{{ getStatsNormalized().all }}</span>
                <span v-else>{{ systemStats.chatbot }}</span>
            </div>
        </div>
        <div class="colors" v-if="!chatListToolbarLocked">
            <div class="color">
                <div class="color-circle" style="background-color: #32cd32"></div>
                <div class="color-text">{{ $t("chatWindow.you") }}</div>
            </div>
            <div class="color">
                <div class="color-circle" style="background-color: #ffa500"></div>
                <div class="color-text">{{ $t("chatWindow.others") }}</div>
            </div>

            <div class="color">
                <div class="color-circle" style="background-color: #9370db"></div>
                <div class="color-text">{{ $t("chatWindow.queue") }}</div>
            </div>
            <div class="color">
                <div class="color-circle" style="background-color: #87ceeb"></div>
                <div class="color-text">{{ $t("chatWindow.chatbotContact") }}</div>
            </div>
            <div class="color">
                <div class="color-circle" style="background-color: #d3d3d3"></div>
                <div class="color-text">{{ $t("chatWindow.closedContact") }}</div>
            </div>
        </div>
        <div class="conversation-lists" ref="conversations" @scroll="scrollListener">
            <div class="conversation-lists-items" v-if="contactListGroupBy === 'none'">
                <GridContactListItem
                    v-for="(c, index) of contactList"
                    :contact="c"
                    :key="(c && c._id) || index"
                    :activeContact="contact"
                    @joinConversation="joinConversation(c)"
                />
            </div>
            <div class="chat-groups" v-else>
                <div class="group" v-for="(group, index) of getGroupContacts()" :key="index">
                    <div class="group-toolbar">
                        <div class="group-title">
                            <AgentAvatar
                                :userID="group.group.id"
                                :size="20"
                                v-if="group.group.type === 'agent'"
                                single-char
                                status-background-color
                            />
                            {{ group.group.label }}
                        </div>
                        <div class="actions">
                            <i
                                class="material-icons"
                                @click="addUserToList(group.group.id)"
                                v-if="group.group.type === 'agent' && !isUserInList(group.group.id)"
                                >zoom_in</i
                            >
                            <i
                                class="material-icons"
                                :class="{ 'text-danger': isUserInList(group.group.id) }"
                                @click="removeUserFromList(group.group.id)"
                                v-if="group.group.type === 'agent' && isUserInList(group.group.id)"
                                >zoom_out</i
                            >

                            <i class="material-icons" @click="switchToMultiViewMode(group.group.id)">visibility</i>
                            <!--                            <i class="material-icons">more_vert</i>-->
                        </div>
                    </div>

                    <div class="conversation-lists-items">
                        <GridContactListItem
                            v-for="(c, index) of group.contacts"
                            :contact="c"
                            :key="group.group.id + index"
                            :activeContact="contact"
                            @joinConversation="joinConversation(c)"
                        />
                    </div>
                </div>
            </div>
            <div class="empty-conversations" v-if="contactList.length <= 0">
                <div class="empty-conversations-content">
                    <NoConversations />
                    <div class="empty-conversations-content-title">{{ $t("message.noActiveChat") }}.</div>
                    <div class="empty-conversations-content-description">
                        {{ $t("message.noConversationDesc") }}
                    </div>
                    <div class="empty-conversations-content-action">
                        <SupsisButton @click="forwardCustomerUrl" primary
                            >{{ $t("message.customerPage") }}
                        </SupsisButton>
                    </div>
                </div>
            </div>
        </div>
    </ResizableContent>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import GridContactListItem from "./GridContactListItem.vue";
import ContactListFilters from "./ContactListFilters.vue";
import ResizableContent from "./ResizableContent.vue";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import NoConversations from "./svg/NoConversations.vue";
import Back from "../../svg/fastWizard/Back.vue";
import Header from "../../Header.vue";
import DisplayConfig from "../../fast-initialize/DisplayConfig.vue";
import ContactListFilterToolBar from "./ContactListFilterToolBar.vue";
import CustomPicker from "../../pickers/CustomPicker.vue";
import ContactSorter from "./contact-sorter/ContactSorter.vue";
import ContactSwitcherButton from "./ContactSwitcherButton.vue";
import log2 from "../../../utils/log2";
import AgentAvatar from "../../common/AgentAvatar.vue";
import ContactGroupButton from "./contact-group/ContactGroupButton.vue";

const log = log2("GridContactList");

export default {
    name: "GridContactList",
    components: {
        ContactGroupButton,
        AgentAvatar,
        ContactSwitcherButton,
        ContactSorter,
        CustomPicker,
        DisplayConfig,
        Header,
        Back,
        NoConversations,
        SupsisButton,
        ResizableContent,
        GridContactListItem,
        ContactListFilters,
        ContactListFilterToolBar,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        contactList: {
            type: Array,
            default: () => [],
        },
        toggleConversation: {
            type: Function,
            default: () => _.noop,
        },
        maxGridConversationsWidth: {
            type: Number,
            default: 0,
        },
        minGridConversationsWidth: {
            type: Number,
            default: 0,
        },
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users.data,
            departments: (state) => state.subject.subjects.data,
            instagramChannels: (state) => state.channel.channels.filter((x) => ["instagram"].includes(x.type)),
            messengerChannels: (state) => state.channel.channels.filter((x) => ["messenger"].includes(x.type)),
            whatsappChannels: (state) =>
                state.channel.channels.filter((x) =>
                    ["whatsapp-v1", "whatsapp-qr", "whatsapp-v2", "whatsapp-business-api"].includes(x.type)
                ),
            lastMessages: (state) => state.chat.lastMessages,
            watchingContactIds: (state) => state.chat.watchingContactIds,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            paginationCursor: (state) => state.chat.contactPaginationCursor,
            contactListFilters: (state) => state.chat.contactListFilters,
            contactListGroupBy: (state) => state.chat.contactListGroupBy,
            contactListSortMode: (state) => state.chat.contactListSortMode,
            contactListStore: (state) => state.chat.contactLists,

            contactListLoading: (state) => state.chat.contactListLoading,
            visitorScreenUrl: (state) => {
                const isProduction = process.env.ENVIRONMENT === "production";
                const isStage = process.env.ENVIRONMENT === "stage";
                let siteAlias = (state.site.site.customerClientAddress || "").replace(".visitor.supsis.live", "");
                const protocol = isProduction || isStage ? "https" : "http";
                if (siteAlias === "visitor.supsis.live") siteAlias = "demo-site.supsis.live?customer=supsis";
                else siteAlias = "demo-site.supsis.live?customer=" + siteAlias;
                const hostname = isProduction
                    ? siteAlias
                    : isStage
                    ? `demo-site.supsis.live?url=${protocol}://${
                          state.site.site.customerClientAddress || "betavisitor.supsis.live"
                      }/static/js/loader.js`
                    : "localsite.supsis.live?customer=local";
                return `${protocol}://${hostname}`;
            },
            systemStats: (state) => state.chat.stats,
            contactListFilterMode: (state) => state.chat.contactListFilterMode,
            chatListToolbarLocked: (state) => state.chat.chatListToolbarLocked,
            selectedCustomFilterTemplate: (state) => state.chat.selectedCustomFilterTemplate,
        }),
    },
    data: function () {
        return {
            searchMode: false,
            query: "",
            unread: "",
            searchActive: false,
            filteredConversations: [],
            initedFirstChat: false,
            width: 400, //locked=false ise 510 olması lazım
            cloneContactList: [],
            offset: {
                start: 0,
                end: 15,
            },
            filters: {},
        };
    },
    mounted() {
        this.filters = { ...this.contactListFilters };
    },
    methods: {
        ...mapActions({
            unWatchContactConversation: "chat/unWatchContactConversation",
            watchContactConversation: "chat/watchContactConversation",
            setContactPaginationOffset: "chat/setContactPaginationOffset",
            updateContactFilters: "chat/updateContactFilters",
            findContact: "chat/findContact",
            getContactById: "chat/getContactById",
            setMultiChatViewerMode: "chat/setMultiChatViewerMode",
            setContactListFilterMode: "chat/setContactListFilterMode",
            setChatListToolbarLocked: "chat/setChatListToolbarLocked",
        }),
        toggleLock() {
            this.setChatListToolbarLocked(!this.chatListToolbarLocked);
        },
        switchToMultiViewMode(agentId) {
            this.setMultiChatViewerMode({
                enabled: true,
                value: agentId,
                type: "agent",
            });
        },
        removeUserFromList(userId) {
            log("removeUserFromList - updateContactFilters", userId, this.contactListFilters);
            this.updateContactFilters({
                ...this.contactListFilters,
                lastChatOwner: (this.contactListFilters.lastChatOwner || []).filter((x) => x.value !== userId),
                selectedInteractions: (this.contactListFilters.selectedInteractions || []).filter(
                    (x) => x.value !== userId
                ),
            });
        },
        addUserToList(userId) {
            log("addUserToList - updateContactFilters", userId, this.contactListFilters);
            this.updateContactFilters({
                ...this.contactListFilters,
                lastChatOwner: [
                    ...(this.contactListFilters.lastChatOwner || []),
                    {
                        type: "agent",
                        value: userId,
                    },
                ],
                selectedInteractions: [
                    ...(this.contactListFilters.selectedInteractions || []),
                    {
                        type: "agent",
                        value: userId,
                    },
                ],
            });
        },
        isUserInList(userId) {
            return (
                (this.contactListFilters.lastChatOwner || []).find((x) => x.value === userId) &&
                (this.contactListFilters.selectedInteractions || []).find((x) => x.value === userId)
            );
        },

        getGroupContacts() {
            const groupBy = this.contactListGroupBy;
            let items = [];
            const randomId = parseInt(Math.random() * 1000);
            const addToList = (contact, group) => {
                log(`addToList group=${group.id} contact=${contact._id}`);
                items.push({
                    contact,
                    group,
                });
            };
            log("this.contactList agent count debug", {
                users: this.users.map((x) => {
                    return [x._id, x.name];
                }),
                agents: this.contactList.map((x) => x.lastConversation?.users || []).flat().length,
                agents2: this.contactList.map((x) => [x._id, x.lastConversation?.users || []]).flat(),
            });
            if (groupBy === "agent") {
                this.contactList.forEach((contact) => {
                    const users = contact.lastConversation?.users || [];
                    const isChatbot = contact.lastConversation?.status === "chatbot";
                    const inQueue = users.length === 0 || contact.lastConversation?.status === "new";
                    const group = {
                        id: "",
                        type: "agent",
                        label: "",
                        order: 0,
                    };
                    if (isChatbot) {
                        group.id = "chatbot";
                        group.type = "chatbot";
                        group.label = "Chatbot";
                        group.order = -2;
                        addToList(contact, group);
                    } else if (inQueue) {
                        group.id = "queue";
                        group.type = "queue";
                        group.label = "Kuyruktakiler";
                        group.order = -1;
                        addToList(contact, group);
                    } else {
                        const userOrdersMaps = _.chain(this.users)
                            .orderBy("name", "asc")
                            .map((x, i) => [x, i])
                            .fromPairs()
                            .value();

                        users.forEach((userId) => {
                            group.id = userId;
                            group.label = this.users.find((x) => x._id === userId)?.name || "";
                            group.order = this.user._id === userId ? 10000 : userOrdersMaps[userId] || 0;

                            addToList(contact, _.cloneDeep(group));
                        });
                    }
                });
            } else if (groupBy === "department") {
                const departmentOrdersMaps = _.chain(this.departments)
                    .orderBy("name", "asc")
                    .map((x, i) => [x, i])
                    .fromPairs()
                    .value();
                this.contactList.forEach((contact) => {
                    const departmentId = contact.lastConversation?.subject;
                    const department = departmentId ? this.departments.find((x) => x._id === departmentId) : null;
                    const group = {
                        id: departmentId,
                        type: "department",
                        label: department?.name || "Departmansız",
                        order: departmentOrdersMaps[departmentId] || 0,
                    };
                    addToList(contact, group);
                });
            }
            log(`getGroupContacts items before 1`, [...items]);
            items = _.orderBy(items, ["group.order", "group.label"], ["desc", "asc"]).reduce((acc, x) => {
                const group = x.group;
                const contact = x.contact;
                const existingGroup = acc.find((item) => item.group.id === group.id);

                if (existingGroup) {
                    existingGroup.contacts.push(contact);
                } else {
                    acc.push({
                        group,
                        contacts: [contact],
                    });
                }

                return acc;
            }, []);
            log(`getGroupContacts items before 2`, [...items]);
            items.forEach((x) => {
                // x.contacts = _.uniqBy(x.contacts, "_id");
                if (this.contactListSortMode === "message.desc") {
                    x.contacts = x.contacts.sort(
                        (a, b) =>
                            (b.lastMessage && new Date(b.lastMessage.createdDate)) -
                            (a.lastMessage && new Date(a.lastMessage.createdDate))
                    );
                } else if (this.contactListSortMode === "chat.create.desc") {
                    x.contacts = x.contacts.sort((a, b) => {
                        const date1 = a.lastConversation.createdDate || a.lastInteractionDate;
                        const date2 = b.lastConversation.createdDate || b.lastInteractionDate;
                        return new Date(date2) - new Date(date1);
                    });
                } else if (this.contactListSortMode === "chat.create.asc") {
                    x.contacts = x.contacts.sort((a, b) => {
                        const date1 = a.lastConversation.createdDate || a.lastInteractionDate;
                        const date2 = b.lastConversation.createdDate || b.lastInteractionDate;
                        return new Date(date1) - new Date(date2);
                    });
                }
            });
            log(`getGroupContacts items before 3`, [...items]);
            log("getGroupContacts", { items, total: this.contactList.length, result: items.length });

            return items;
        },
        querySearch: _.debounce(function (e) {
            this.filters = {
                ...this.filters,
                query: e.target.value,
            };
        }, 500),
        closeSearchMode() {
            this.searchMode = false;
            this.querySearch({ target: { value: "" } });
        },
        getStatsNormalized() {
            const stats = _.cloneDeep(this.paginationCursor.stats);
            if (this.filters.showUnread) {
                stats.unread =
                    stats.unread < this.contactListStore.length ? this.contactListStore.length : stats.unread;

                if (stats.all === 0) {
                    stats.all = this.contactListStore.filter((x) => x.unreadedCount > 0).length;
                }
            } else {
                stats.all = stats.all < this.contactListStore.length ? this.contactListStore.length : stats.all;
                if (stats.unread === 0) {
                    stats.unread = this.contactListStore.filter((x) => x.unreadedCount > 0).length;
                }
            }

            log("getStatsNormalized", {
                old: this.paginationCursor.stats,
                new: stats,
            });
            return stats;
        },
        searchModeOpen() {
            this.searchMode = true;
        },
        scrollTop() {
            const el = document.querySelector(".conversation-lists");
            if (el) el.scrollTop = 0;
        },
        resetFilters() {
            this.scrollTop();
            this.setContactPaginationOffset({
                skip: 0,
                limit: this.paginationCursor.offset.limit,
            });
        },
        showAll() {
            this.setContactListFilterMode("custom");
            this.resetFilters();
            this.filters.showUnread = false;
        },
        showUnread() {
            this.setContactListFilterMode("custom");
            this.resetFilters();
            this.filters.showUnread = true;
        },
        showQueue() {
            this.setContactListFilterMode("queue");
        },
        showChatbot() {
            this.setContactListFilterMode("chatbot");
        },
        forwardCustomerUrl() {
            window.open(this.visitorScreenUrl, "_blank");
        },
        isActive(contact) {
            return this.contact && this.contact._id === contact._id;
        },
        isAssignToMe(contact) {
            return contact.lastConversation.users.includes(this.user._id);
        },
        async joinConversation(contact) {
            const inited = this.$_.get(contact, "inited", false);
            if (!inited) contact = await this.getContactById({ contactId: contact._id });

            const isAssignToMe = this.isAssignToMe(contact);
            if (this.watchingContactIds.length > 0) this.unWatchContactConversation(contact);
            if (isAssignToMe || this.isActive(contact)) {
                this.toggleConversation(contact);
            } else if (!this.isActive(contact)) {
                this.watchContactConversation(contact);
            }
        },
        onResizeConversations(handle, x, y, width) {
            width = Math.max(width, this.minGridConversationsWidth);
            if (width !== this.width) this.width = width;
        },
        scrollListener() {
            if (this.contactListLoading) return;
            const el = this.$refs.conversations;
            const isTheEnd = el.offsetHeight + el.scrollTop + 10 >= el.scrollHeight;
            if (isTheEnd) {
                const selectedTab = {
                    [this.filters.showUnread]: "unread",
                };
                const isAll = !this.filters.showUnread;
                const stats = this.paginationCursor.stats;
                const limit = isAll ? stats.all : stats[selectedTab[true]];
                const isDone = this.paginationCursor.offset.skip + this.paginationCursor.offset.limit >= limit;
                if (isDone) return;
                this.setContactPaginationOffset({
                    skip: Math.max(
                        Math.min(
                            this.paginationCursor.offset.skip + this.paginationCursor.offset.limit,
                            limit - this.paginationCursor.offset.limit
                        ),
                        0
                    ),
                    limit: this.paginationCursor.offset.limit,
                });
            }
        },
    },
    watch: {
        chatListToolbarLocked(isLocked) {
            if (isLocked && this.width === 510) {
                this.width = 400;
            }
            if (!isLocked && this.width < 510) {
                this.width = 510;
            }
        },
        width() {
            this.$emit("width", this.width);
            localStorage.setItem("grid-conversation-width", this.width);
        },
        maxGridConversationsWidth() {
            const width = parseInt(
                localStorage.getItem("grid-conversation-width") || `${this.maxGridConversationsWidth * 0.5},`
            );
            this.width = Math.max(width, this.minGridConversationsWidth);
        },
        filters: {
            deep: true,
            handler: _.debounce(function (newVal, oldVal) {
                const oldValEmpty = Object.keys(oldVal).length === 0;
                if (!oldValEmpty && !_.isEqual(this.filters, this.contactListFilters)) {
                    log("watch filters - updateContactFilters");

                    this.updateContactFilters(this.filters);
                }
            }, 100),
        },
        contactListFilters: {
            deep: true,
            handler() {
                const isEquals = _.isEqual(this.filters, this.contactListFilters);
                if (!isEquals) this.filters = { ...this.contactListFilters };
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.conversations {
    width: 100%;
    height: 100% !important;
    border-right: 1px solid var(--color-border-default);
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .head-content {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 100%;

        .search-area {
            display: flex;
            gap: 7px;
            align-items: center;

            .search-close-wrapper {
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                background-color: #f1f4f7;
                padding: 3px;

                .material-icons {
                    font-size: 20px;
                }
            }

            .chat-search-wrapper {
                width: 100%;

                .search-input {
                    display: flex;
                    align-items: center;
                    border-radius: 6px;
                    padding-right: 7px;
                    height: 30px;
                    width: 100%;
                    color: #000 !important;

                    input {
                        background-color: #f1f4f7;
                        color: #000 !important;
                        font-weight: normal;
                        border: none !important;
                    }

                    &:hover,
                    &:active,
                    &:focus {
                        box-shadow: none;
                    }

                    .material-icons {
                        margin-right: 5px;
                        font-size: 20px;
                    }

                    .ant-input-prefix {
                        left: 5px;
                    }
                }
            }
        }

        .title-content {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            width: 100%;

            .title {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 5px;
                flex: 1 1 auto;

                .title-text {
                    font-weight: bold;
                    font-size: 18px;
                }
            }

            .actions {
                display: flex;
                align-items: center;
                gap: 3px;

                .actions-item {
                    cursor: pointer;
                    border-radius: 2px;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    user-select: none;

                    &.active {
                        background: var(--primary-default-button-color);
                        color: var(--white);
                    }

                    &:not(.active) {
                        &:hover {
                            background: var(--color-bg-neutral-default);
                        }
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }

        .material-icons {
            font-size: 18px;
        }

        .secondary-content {
            display: flex;
            align-items: center;
            gap: 5px;

            .btnLock {
                cursor: pointer;
                font-size: 20px;
            }

            .action-tabs {
                flex: 0 100%;
                width: 100%;
                height: 30px;
                background: var(--color-bg-neutral-default);
                border-radius: 5px;
                padding: 2px;
                overflow: hidden;

                &-items {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    height: 100%;
                    overflow-x: auto;

                    &::-webkit-scrollbar {
                        display: none;
                    }
                }

                &.minimize {
                    display: none;
                    flex: 0;
                }
            }

            .chat-search-content {
                flex: 1;
                display: flex;
                align-items: center;

                .supsis-button {
                    z-index: 2;
                    min-width: 40px;
                    max-width: 40px;
                    max-height: 30px;
                    height: 30px;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    line-height: 0;
                    background-color: #f1f4f7 !important;
                    border: none;
                    color: #000;

                    &:hover {
                        background: #4e5256;
                        opacity: 1;
                    }
                }

                &.minimize {
                    .chat-search-input {
                        display: none;
                    }
                }
            }
        }
    }

    .filters-tabs {
        display: flex;
        gap: 3px;
        overflow-x: auto;
        padding: 10px 0;
        align-items: center;
        overflow-y: hidden;

        &::-webkit-scrollbar {
            display: none;
        }

        .filter-tab {
            padding: 1px 3px;
            background-color: var(--color-bg-neutral-default);
            cursor: pointer;
            border-radius: 6px;
            font-size: 14px;
            width: fit-content;
            height: max-content;
            white-space: nowrap;
            display: flex;
            align-items: center;
            gap: 3px;

            span {
                background: var(--white);
                color: #5625cc;
                padding: 2px 5px;
                border-radius: 20px;
            }

            &.active {
                background-color: #eee7ff;
            }
        }
    }

    .colors {
        display: flex;
        gap: 3px;

        .color {
            display: flex;
            align-items: center;
            gap: 3px;

            .color-circle {
                width: 10px;
                height: 10px;
                border-radius: 50%;
            }

            .color-text {
                font-size: 14px;
            }
        }
    }

    .conversation-lists {
        overflow-x: hidden;
        overflow-y: auto;
        width: 100%;
        height: 100%;

        &:not(:first-child) {
            .conversation-item {
                margin-top: 2px;
            }
        }

        &::-webkit-scrollbar {
            display: none;
        }

        &:hover {
            &::-webkit-scrollbar {
                display: block !important;
            }
        }

        .chat-groups {
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 1px;

            .group {
                display: flex;
                flex-direction: column;
                width: 100%;

                .group-toolbar {
                    padding: 3px;
                    background: #f4f4f4;
                    display: flex;
                    justify-content: space-between;

                    .group-title {
                        font-size: 16px;
                        font-weight: bold;
                        color: #8796af;
                        display: flex;
                        align-items: center;
                        gap: 5px;

                        .vue-avatar--wrapper {
                            span {
                                line-height: 10px;
                                font-size: 14px;
                            }
                        }
                    }

                    .actions {
                        display: flex;
                        gap: 5px;
                        align-items: center;

                        svg {
                            fill: #555555;
                        }

                        .material-icons {
                            cursor: pointer;
                            font-size: 20px;
                        }
                    }
                }

                .conversation-lists-items {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    height: 100%;
                }
            }
        }
    }

    .empty-conversations {
        display: flex;
        width: 100%;
        height: 100%;
        align-items: center;
        justify-content: center;
        user-select: none;

        &-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 8px;
            color: #8796af; //TODO: color selector.
            &-title {
                font-size: 18px;
                font-weight: bold;
                margin-top: 10px;
            }

            &-description {
                font-size: 14px;
            }

            &-action {
                margin-top: 10px;

                .supsis-button {
                    border-radius: 100px;
                }
            }
        }
    }

    .user-icon {
        display: inline-block;
        width: 20px;
        height: 20px;
        line-height: 20px;
        border-radius: 50%;
        text-align: center;
        color: white;
        font-size: 12px;
        margin-right: 8px;
    }
}
</style>
<style lang="scss">
.group-title {
    .vue-avatar--wrapper {
        span {
            line-height: 10px;
            font-size: 14px;
        }
    }
}
</style>
